/**
 * The Story: the same stations as one vertical line, oldest first, grouped by year -- the
 * phone's map, and a view of its own on the desktop (a centred column). An interchange is a
 * taller pill; a finding sits at its check's date. Each change names its line with a dot in
 * that line's colour; the title is UKG's reason, with its code beside it.
 */
import { useRef } from 'react'
import { dayMs, fmtDate, fmtMoney, changeText } from './lifelineModel'
import { LineDot } from './Marks'

const RAIL = {
  hire: 'var(--ll-job)', promotion: 'var(--ll-job)', job_change: 'var(--ll-job)', rate_change: 'var(--ll-pay)',
  manager_change: 'var(--ll-manager)', location_change: 'var(--ll-location)', transfer: 'var(--ll-location)',
  leave: 'var(--ll-status)', return: 'var(--ll-status)', termination: 'var(--ll-status)', rehire: 'var(--ll-job)',
}

export default function LifelineVertical({ data, stations, markers, preEvents = [], onSelect }) {
  const endRef = useRef(null)
  const items = [
    ...preEvents.map(e => ({ kind: 'pre', day: e.date, e })),
    ...stations.map(s => ({ kind: 'station', day: s.date, s })),
    ...markers.filter(m => m.day).map(m => ({ kind: 'finding', day: m.check?.pay_date || m.day, m })),
  ].sort((a, b) => dayMs(a.day) - dayMs(b.day))
  if (!items.length) {
    return <p className="ll-na"><b>No stations yet.</b> {data.sections.events?.reason || 'Nothing in this person’s history has changed.'}</p>
  }
  let year = null
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span className="ll-eyebrow">Oldest first · {items.length} stations</span>
        <button type="button" className="ll-link" style={{ minHeight: 44 }}
                onClick={() => endRef.current?.scrollIntoView({ block: 'center' })}>Jump to today</button>
      </div>
      <ol className="ll-vertical" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {items.map((it, i) => {
          const y = new Date(dayMs(it.day)).getUTCFullYear()
          const head = y !== year ? (year = y, <li key={`y${y}`} className="ll-vyear" aria-hidden="true">{y}</li>) : null
          if (it.kind === 'pre') {
            return [head, (
              <li key={it.e.id} className="ll-vstation" style={{ '--ll-rail': 'var(--ll-recruiting)' }}>
                <div className="ll-vrail"><span className="ll-vdot" /></div>
                <div className="ll-vbody">
                  <span className="ll-mono ll-note">{fmtDate(it.day, false)} · before hire</span>
                  <span className="ll-vtitle">{it.e.title}</span>
                  {it.e.detail && <span className="ll-note">{it.e.detail}</span>}
                </div>
              </li>
            )]
          }
          if (it.kind === 'finding') {
            const f = it.m.finding
            return [head, (
              <li key={`f${i}`} className="ll-vstation" style={{ '--ll-rail': 'var(--ll-pay)' }}>
                <div className="ll-vrail"><span className="ll-vdot ll-vdot--finding" /></div>
                <div className="ll-vbody">
                  <span className="ll-mono ll-note">{fmtDate(it.day, false)}</span>
                  <button type="button" className="ll-link" style={{ textAlign: 'left', minHeight: 44 }}
                          onClick={() => onSelect({ type: 'finding', id: it.m.id })}>
                    {f.title || 'Finding'}
                  </button>
                  <span className="ll-note">{it.m.severity === 'high' ? 'High' : it.m.severity === 'medium' ? 'Medium' : 'Low'}{it.m.check ? ` · check ${fmtDate(it.m.check.pay_date)} · gross ${fmtMoney(it.m.check.gross)}` : ''}</span>
                </div>
              </li>
            )]
          }
          const s = it.s
          return [head, (
            <li key={s.id} className="ll-vstation" style={{ '--ll-rail': RAIL[(s.kinds || [])[0]] || 'var(--ll-job)' }}>
              <div className="ll-vrail"><span className={`ll-vdot${s.interchange ? ' ll-vdot--x' : ''}`} /></div>
              <div className="ll-vbody">
                <span className="ll-mono ll-note">{fmtDate(s.date, false)}</span>
                <span className="ll-vtitle">{s.title}{s.reason && s.reason_code ? <span className="ll-mono ll-code">{s.reason_code}</span> : null}{s.interchange ? <span className="ll-vcount"> · {s.changes.length} changes at once</span> : ''}</span>
                {!s.reason && s.reason_code && <span className="ll-note">Reason code <span className="ll-mono">{s.reason_code}</span> (no description on record)</span>}
                <div className="ll-vchips">
                  {(s.changes || []).map((c, k) => <span key={k} className="ll-vchip"><LineDot line={c.line} />{changeText(c)}</span>)}
                </div>
              </div>
            </li>
          )]
        })}
        <li ref={endRef} className="ll-vstation" style={{ '--ll-rail': 'var(--ll-ink)' }}>
          <div className="ll-vrail"><span className="ll-vdot" /></div>
          <div className="ll-vbody"><span className="ll-vtitle">Today</span><span className="ll-mono ll-note">{fmtDate(data.as_of)}</span></div>
        </li>
      </ol>
    </div>
  )
}
